// ─── Local (per-device) identity-key storage for Secure Chat ─────────────────
// See CHAT_E2E_DESIGN.md §3. This is the "local IndexedDB" half of the key
// lifecycle: once a device has generated or recovered a user's chat keypair,
// it's cached here so the user isn't re-prompted for their recovery
// passphrase on every page load — only on a genuinely new device or after
// browser data is cleared.
//
// IMPORTANT: this module stores the PRIVATE key in plaintext, on-device, in
// IndexedDB. That is intentional and safe within this design's threat model —
// the whole point of "this device now has the key" is that it can decrypt
// without re-deriving from a passphrase every time, the same trade-off every
// E2E chat app (Signal, WhatsApp Desktop, etc.) makes for its local key store.
// What must never happen is this key leaving the device other than through
// the deliberate passphrase-wrapped backup path in crypto/chat.js — this
// module makes no network calls and never will.
//
// Keyed by userId (not companyId) since a browser profile only ever
// represents one signed-in user's keys at a time in this app.

const DB_NAME = 'secureChatKeys';
const DB_VERSION = 1;
const STORE_NAME = 'keypairs';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// Returns { userId, publicKey, privateKey } for this device, or null if this
// device has never set up or unlocked chat for this user.
export async function getLocalKeypair(userId) {
  if (!userId) return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(userId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    // IndexedDB can be unavailable in some contexts (e.g. private browsing in
    // some browsers) — fail safe by treating it as "no local key found"
    // rather than crashing the chat UI; the user will just see the
    // setup/unlock modal again, which is a correctness-preserving fallback.
    console.error('[chat keyStore] failed to read local keypair', err);
    return null;
  }
}

export async function saveLocalKeypair(userId, { publicKey, privateKey }) {
  if (!userId || !publicKey || !privateKey) {
    throw new Error('saveLocalKeypair requires userId, publicKey, and privateKey');
  }
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ userId, publicKey, privateKey });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Not currently wired into any UI — kept for a future "log out / forget this
// device's chat key" affordance (e.g. on account sign-out, or a deliberate
// "remove my chat access from this computer" Settings action).
export async function clearLocalKeypair(userId) {
  if (!userId) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(userId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
